// Cliente de la edición de video con IA (Gemini Omni Flash).
//
// Feature NUEVA e independiente del editor de Sora (`editar_vid`): NO lo
// reemplaza. Flujo async en 2 pasos (mismo patrón que traducción/borrado):
//   1) START → dispara el job y devuelve un jobId (n8n corre Gemini en
//      background y sube el mp4 editado a `video-project-assets`).
//   2) STATUS → se polea hasta `done` (con el storage path) o `error`.
//
// Solo para clips ≤10s (límite del modelo). Ver ADR-500 y
// docs/2026-07-07-gemini-omni-editar-clips-timeline.md.
import { supabase } from './supabase';
import {
  GENERATE_AUTH_TOKEN,
  OMNI_EDIT_START_URL,
  OMNI_EDIT_STATUS_URL,
} from '../config';

export type OmniAspectRatio = '16:9' | '9:16' | '1:1' | '4:5';
export type OmniEditStatus = 'processing' | 'done' | 'error';

export interface OmniEditParams {
  userId: string;
  companyId: string;
  projectId: string;
  /** Signed URL (descargable) del clip a editar. */
  clipUrl: string;
  instruction: string;
  aspectRatio: OmniAspectRatio;
  /** Duración del clip en segundos (define el costo en créditos: 1 cr/s). */
  durationSec: number;
}

export interface OmniEditResult {
  jobId: string;
  /** Storage path del video editado dentro del bucket `video-project-assets`. */
  path: string;
  /** Signed URL lista para reproducir el resultado. */
  signedUrl: string;
}

const BUCKET = 'video-project-assets';
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 120; // ~6 min de margen (Gemini ~90s + red)

function sleep(ms: number): Promise<void> {
  return new Promise((r) => window.setTimeout(r, ms));
}

/**
 * Costo en créditos de una edición: 1 crédito por segundo, mínimo 1.
 * Espejo de la tarifa del backend (`calcular gasto de creditos` en n8n).
 */
export function creditsForOmniEdit(durationSec: number): number {
  return Math.max(1, Math.ceil(durationSec));
}

/**
 * Dispara la edición y polea el status hasta que termina. Devuelve el path +
 * signed URL del video editado. Lanza Error si falla o si hace timeout.
 * `onStatus` reporta las transiciones (para la UI).
 */
export async function omniEditVideo(
  params: OmniEditParams,
  onStatus?: (s: OmniEditStatus) => void,
): Promise<OmniEditResult> {
  const jobId = crypto.randomUUID();

  // ── 1) START ──────────────────────────────────────────────────────────
  const startRes = await fetch(OMNI_EDIT_START_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: GENERATE_AUTH_TOKEN,
    },
    body: JSON.stringify({
      user_id: params.userId,
      company_id: params.companyId,
      project_id: params.projectId,
      job_id: jobId,
      clip_url: params.clipUrl,
      instruction: params.instruction,
      aspect_ratio: params.aspectRatio,
      duration_sec: params.durationSec,
    }),
  });
  if (!startRes.ok) {
    throw new Error(`Error al iniciar la edición (${startRes.status}).`);
  }
  const startRaw = await startRes.text();
  let startJson: { response?: string; jobId?: string; error?: string };
  try {
    startJson = JSON.parse(startRaw);
  } catch {
    throw new Error('Respuesta inválida del servidor de edición.');
  }
  if (startJson.response !== 'success') {
    throw new Error(startJson.error || 'No se pudo iniciar la edición.');
  }
  onStatus?.('processing');

  // ── 2) POLL STATUS ────────────────────────────────────────────────────
  for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
    await sleep(POLL_INTERVAL_MS);
    let st: { status?: string; path?: string; error?: string };
    try {
      const stRes = await fetch(OMNI_EDIT_STATUS_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: GENERATE_AUTH_TOKEN,
        },
        body: JSON.stringify({ job_id: jobId }),
      });
      if (!stRes.ok) continue; // reintentar el poll
      st = await stRes.json();
    } catch {
      continue; // error de red puntual → reintentar
    }

    if (st.status === 'done' && st.path) {
      const { data: urlData } = await supabase.storage
        .from(BUCKET)
        .createSignedUrl(st.path, 3600);
      if (!urlData?.signedUrl) {
        throw new Error('Video editado pero no se pudo obtener la URL.');
      }
      onStatus?.('done');
      return { jobId, path: st.path, signedUrl: urlData.signedUrl };
    }
    if (st.status === 'error') {
      onStatus?.('error');
      throw new Error(st.error || 'La edición del video falló.');
    }
    // status === 'processing' → seguir poleando
  }

  throw new Error('La edición del video tardó demasiado. Reintentá.');
}
